export const SLIDING_APPEARANCE_KEY = "sliding.appearance";

export const SLIDING_APPEARANCE_LIGHT = "light";
export const SLIDING_APPEARANCE_DARK = "dark";

const SLIDING_APPEARANCE_MODES = new Set([SLIDING_APPEARANCE_LIGHT, SLIDING_APPEARANCE_DARK]);

/** Dark theme base — navy (matches home premium pages). */
const DARK_BASE = "#0B1424";
const DARK_TOP = "#12203A";
const DARK_BOTTOM = "#070E1A";
const DARK_CARD = "#15253F";
const DARK_NAV = "#0A1220";

export const slidingAppearanceTokens = {
    [SLIDING_APPEARANCE_LIGHT]: {
        pageBg: "#f2f2f7",
        pageBackground: null,
        cardBg: "#ffffff",
        cardBackground: null,
        text: "#0f1419",
        textMuted: "rgba(15, 23, 42, 0.55)",
        textSubtle: "rgba(15, 23, 42, 0.45)",
        divider: "rgba(15, 23, 42, 0.12)",
        sectionHeader: "rgba(15, 23, 42, 0.55)",
        shellBg: "#ffffff",
        shellBackground: null,
        navBg: "#ffffff",
        navBackground: null,
        navBorder: "rgba(15, 23, 42, 0.1)",
        cardShadow: "0 4px 24px rgba(15, 23, 42, 0.08)",
        navInactive: "rgba(15, 23, 42, 0.45)",
        error: "#b91c1c",
        warning: "#b45309",
        elevatedCardBorder: "1px solid rgba(15, 23, 42, 0.08)",
        glassCardBg: "rgba(255, 255, 255, 0.72)",
        glassCardBorder: "1px solid rgba(255, 255, 255, 0.65)",
        pillInactiveBg: "rgba(15, 23, 42, 0.08)",
        pillInactiveText: "rgba(15, 23, 42, 0.72)",
        segmentTrackBg: "rgba(15, 23, 42, 0.08)",
        segmentTrackBorder: "rgba(15, 23, 42, 0.1)",
        segmentSelectedBg: "#ffffff",
        segmentSelectedText: "#0f1419",
        segmentInactiveText: "rgba(15, 23, 42, 0.55)",
        miniPillSelectedBg: "rgba(59, 166, 102, 0.16)",
        miniPillSelectedText: "#2e7d32",
        miniPillInactiveBg: "rgba(15, 23, 42, 0.06)",
        miniPillInactiveText: "rgba(15, 23, 42, 0.65)",
        imagePlaceholderBg: "rgba(15, 23, 42, 0.04)",
        iconMuted: "rgba(15, 23, 42, 0.28)",
        iconMutedBg: "rgba(15, 23, 42, 0.05)",
        previewGridGutter: "rgba(15, 23, 42, 0.18)",
        previewGridBorder: "rgba(15, 23, 42, 0.28)",
        previewTileOutline: "rgba(15, 23, 42, 0.22)",
    },
    [SLIDING_APPEARANCE_DARK]: {
        pageBg: DARK_BASE,
        pageBackground: `linear-gradient(180deg, ${DARK_TOP} 0%, ${DARK_BASE} 48%, ${DARK_BOTTOM} 100%)`,
        cardBg: DARK_CARD,
        cardBackground: null,
        text: "#F4F7FB",
        textMuted: "rgba(214, 226, 240, 0.68)",
        textSubtle: "rgba(214, 226, 240, 0.5)",
        divider: "rgba(255, 255, 255, 0.12)",
        sectionHeader: "rgba(214, 226, 240, 0.5)",
        shellBg: DARK_BASE,
        shellBackground: `linear-gradient(180deg, ${DARK_TOP} 0%, ${DARK_BASE} 48%, ${DARK_BOTTOM} 100%)`,
        navBg: DARK_NAV,
        navBackground: `linear-gradient(180deg, ${DARK_BASE} 0%, ${DARK_BOTTOM} 100%)`,
        navBorder: "rgba(46, 196, 182, 0.2)",
        cardShadow: "0 14px 40px rgba(0, 0, 0, 0.4)",
        navInactive: "rgba(214, 226, 240, 0.45)",
        error: "#f87171",
        warning: "#fbbf24",
        elevatedCardBorder: "1px solid rgba(255, 255, 255, 0.12)",
        glassCardBg: "rgba(21, 37, 63, 0.82)",
        glassCardBorder: "1px solid rgba(255, 255, 255, 0.12)",
        pillInactiveBg: "rgba(255, 255, 255, 0.08)",
        pillInactiveText: "rgba(214, 226, 240, 0.72)",
        segmentTrackBg: "rgba(0, 0, 0, 0.28)",
        segmentTrackBorder: "rgba(46, 196, 182, 0.2)",
        segmentSelectedBg: "rgba(46, 196, 182, 0.22)",
        segmentSelectedText: "#7FE8DB",
        segmentInactiveText: "rgba(214, 226, 240, 0.58)",
        miniPillSelectedBg: "rgba(46, 196, 182, 0.22)",
        miniPillSelectedText: "#7FE8DB",
        miniPillInactiveBg: "rgba(255, 255, 255, 0.08)",
        miniPillInactiveText: "rgba(214, 226, 240, 0.72)",
        imagePlaceholderBg: "rgba(0, 0, 0, 0.28)",
        iconMuted: "rgba(214, 226, 240, 0.4)",
        iconMutedBg: "rgba(46, 196, 182, 0.16)",
        previewGridGutter: "rgba(0, 0, 0, 0.52)",
        previewGridBorder: "rgba(46, 196, 182, 0.35)",
        previewTileOutline: "rgba(214, 226, 240, 0.28)",
    },
};

/** Scrollable page body (Library, My Puzzles, Settings) — navy gradient in dark mode. */
export function slidingPageContentSx(tokens) {
    return {
        ...slidingSurfaceSx(tokens, "page"),
        minHeight: "100%",
        pb: 1,
        boxSizing: "border-box",
    };
}

/** Active play board — visible tile seams on light and dark puzzle photos. */
export function slidingPlayGridProps(tokens) {
    return {
        gridGapPx: 3,
        tileBorderRadius: "2px",
        gridGutterColor: tokens.previewGridGutter,
        gridBorderColor: tokens.previewGridBorder,
        tileOutlineColor: tokens.previewTileOutline,
    };
}

/** Static puzzle previews on Home / Library cards — visible grid lines on any photo. */
export function slidingPreviewGridProps(tokens) {
    return {
        animateMoves: false,
        playMoveSound: false,
        gridGapPx: 3,
        tileBorderRadius: "4px",
        gridGutterColor: tokens.previewGridGutter,
        gridBorderColor: tokens.previewGridBorder,
        tileOutlineColor: tokens.previewTileOutline,
    };
}

/** Featured / resume cards on home and library. */
export function slidingElevatedCardSx(tokens) {
    const gradient = tokens.cardBackground;
    return {
        bgcolor: tokens.cardBg,
        ...(gradient ? { background: gradient } : {}),
        border: tokens.elevatedCardBorder,
        boxShadow: tokens.cardShadow,
    };
}

/** MUI `sx` background for shell, grouped page bleed, or nav bar. */
export function slidingSurfaceSx(tokens, surface = "shell") {
    const backgroundKey =
        surface === "page" ? "pageBackground" : surface === "nav" ? "navBackground" : "shellBackground";
    const bgKey = surface === "page" ? "pageBg" : surface === "nav" ? "navBg" : "shellBg";
    const fallback = tokens[bgKey];
    const gradient = tokens[backgroundKey];
    if (gradient) {
        return { bgcolor: fallback, background: gradient };
    }
    return { bgcolor: fallback };
}

export function normalizeSlidingAppearance(value) {
    const mode = String(value ?? "").trim().toLowerCase();
    return SLIDING_APPEARANCE_MODES.has(mode) ? mode : SLIDING_APPEARANCE_LIGHT;
}

export function loadSlidingAppearance() {
    try {
        return normalizeSlidingAppearance(localStorage.getItem(SLIDING_APPEARANCE_KEY));
    } catch {
        return SLIDING_APPEARANCE_LIGHT;
    }
}

export function saveSlidingAppearance(mode) {
    const normalized = normalizeSlidingAppearance(mode);
    try {
        localStorage.setItem(SLIDING_APPEARANCE_KEY, normalized);
    } catch {
        /* ignore quota / private mode */
    }
    return normalized;
}

export function getSlidingAppearanceTokens(mode) {
    return slidingAppearanceTokens[normalizeSlidingAppearance(mode)];
}

export function applySlidingAppearanceToDocument(mode) {
    if (typeof document === "undefined") {
        return;
    }
    const normalized = normalizeSlidingAppearance(mode);
    document.documentElement.setAttribute("data-sliding-appearance", normalized);
    document.documentElement.style.colorScheme = normalized === SLIDING_APPEARANCE_DARK ? "dark" : "light";
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
        themeColorMeta.setAttribute(
            "content",
            normalized === SLIDING_APPEARANCE_DARK ? DARK_BASE : "#ffffff",
        );
    }
}
